
import api, { adminApi } from '../services/api';

// Constants for localStorage
const TOKEN_STORAGE_KEY = 'ankr_pin_tokens'; // Changed key to store an object

/**
 * Stores the PIN-JWT, Refresh Token, State, and Case ID in localStorage.
 * @param {string} pinJwt - The signed PIN-JWT.
 * @param {string} refreshToken - The refresh token.
 * @param {string} state - The state parameter.
 * @param {string} caseId - The case ID.
 */
export const storeTokens = (pinJwt, refreshToken, state, caseId) => {
  if (pinJwt && refreshToken && state && caseId) {
    try {
      const tokens = {
        pin_jwt: pinJwt,
        refresh_token: refreshToken,
        state: state,
        case_id: caseId,
      };
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
      console.log('Tokens stored securely');
    } catch (error) {
      console.error('Error storing tokens:', error);
    }
  }
};

/**
 * Retrieves the stored tokens from localStorage.
 * @returns {object|null} - Object containing tokens or null if not found.
 */
export const getTokensFromStorage = () => {
  try {
    const tokensString = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (tokensString) {
      return JSON.parse(tokensString);
    }
    return null;
  } catch (error) {
    console.error('Error retrieving tokens:', error);
    return null;
  }
};

/**
 * Clears the stored tokens from localStorage.
 */
export const clearTokens = () => {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    console.log('Tokens cleared');
  } catch (error) {
    console.error('Error clearing tokens:', error);
  }
};

/**
 * Comprehensive function to clear all application data from localStorage.
 * This should be used during logout to ensure complete cleanup.
 */
export const clearAllAppData = () => {
  try {
    // Clear all token-related data
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem('pin_jwt');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('case_id');
    localStorage.removeItem('state');
    localStorage.removeItem('ankr_pin_token');
    
    // Clear admin tokens
    localStorage.removeItem('admin_api_token');
    localStorage.removeItem('admin_tokens');
    
    // Clear session and visit data
    localStorage.removeItem('ankr_visit_status');
    
    // Clear app-specific data
    localStorage.removeItem('app_version');
    localStorage.removeItem('audioStates');
    localStorage.removeItem('em9vbS1sb2NhbC1zdG9yYWdl'); // Zoom storage
    // Note: Redux persist handled by persistor.purge() in AuthContext - don't manually remove here

    // Clear VCX session data and history (but keep vcx_session_feedback_submitted and vcx_feedback_tracker to prevent annoying popups)
    localStorage.removeItem('vcx_session_data');
    localStorage.removeItem('vcx_feedback_history');

    // Clear all ankr-prefixed, intake form, and dynamic entries
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('ankr_') ||
          key.startsWith('intakeForm') ||
          key.startsWith('ankr_form_data_') ||
          key.startsWith('ankr_yob_')) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing all app data:', error);
  }
};

// Import the token verification cache from api.js
import { tokenVerificationCache } from '../services/api';

/**
 * Decodes and verifies a signed PIN-JWT by calling the backend verification endpoint.
 * Uses a cache to prevent duplicate requests for the same token.
 * @param {string} pinJwt - The signed PIN-JWT.
 * @returns {Promise<object>} - Promise resolving with the decoded token payload or an error object.
 */
export const decodePinJwt = async (pinJwt) => {
  if (!pinJwt) {
    return { error: 'no_token', message: 'No token provided for decoding.' };
  }

  // Check if we've already verified this token recently (within the last 30 seconds)
  const now = Date.now();
  if (tokenVerificationCache.lastVerifiedToken === pinJwt &&
      tokenVerificationCache.lastVerificationResult &&
      tokenVerificationCache.lastVerificationTime &&
      (now - tokenVerificationCache.lastVerificationTime < 30000)) {
    // console.log('Using cached token verification result');
    return tokenVerificationCache.lastVerificationResult;
  }

  try {
    // console.log('Verifying token with backend...');
    // Call the backend verification endpoint
    const response = await api.post('patient/verify-token/', { token: pinJwt });

    if (response.status === 200) {
      // Backend successfully verified and returned the payload
      // Cache the result
      tokenVerificationCache.lastVerifiedToken = pinJwt;
      tokenVerificationCache.lastVerificationResult = response.data;
      tokenVerificationCache.lastVerificationTime = now;
      return response.data;
    } else {
      // Backend returned an error (e.g., invalid signature, expired)
      console.error('Backend token verification failed:', response.data);
      return { error: response.data.error || 'verification_failed', message: response.data.message || 'Token verification failed.' };
    }

  } catch (error) {
    console.error('Error calling backend token verification endpoint:', error);
    // Handle network errors or other exceptions
    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        return { error: error.response.data.error || 'api_error', message: error.response.data.message || `API error: ${error.response.status}` };
    } else if (error.request) {
        // The request was made but no response was received
        return { error: 'network_error', message: 'Network error during token verification.' };
    } else {
        // Something happened in setting up the request that triggered an Error
        return { error: 'request_error', message: `Request setup error: ${error.message}` };
    }
  }
};


/**
 * Validates the year of birth against the token data (decoded JWT payload).
 * @param {string} inputYear - Year entered by the patient.
 * @param {Object} tokenData - Decoded token payload.
 * @returns {boolean} - Whether the year matches.
 */
export const validateYearOfBirth = (inputYear, tokenData) => {
  // Assuming the decoded JWT payload contains a 'dob' field in 'YYYY-MM-DD' format
  if (!tokenData || !tokenData.patient_info || !tokenData.patient_info.dob) {
      // console.warn("Token data or DOB missing for YOB validation.");
      return false;
  }

  try {
      // Extract year from DOB (format: YYYY-MM-DD)
      const tokenYear = tokenData.patient_info.dob.split('-')[0];
      return inputYear === tokenYear;
  } catch (error) {
      console.error("Error extracting year from DOB:", error);
      return false;
  }
};

// ============================================================================
// EMBED MIRASOL ADMIN PANEL TOKEN FUNCTIONALITY FOR IFRAME INTEGRATION
// ============================================================================
/**
 * Check if running in iframe
 * @returns {boolean} True if running in iframe
 */
export const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

/**
 * Process embed token (exactly like handleManualTokenSubmit in LoginPage.jsx)
 * @param {string} token - The API token to process
 * @returns {Promise<boolean>} Success status
 */
export const processEmbedToken = async (token) => {
  try {
    const response = await adminApi.verifyLoginToken(token);

    if (response.data.success) {
      const { api_token_id, api_token_type, expires_at } = response.data;

      localStorage.setItem('admin_api_token', token);
      localStorage.setItem('admin_tokens', JSON.stringify({
        api_token_id: api_token_id,
        expires_at: expires_at,
        token_type: api_token_type
      }));
      
      // Redirect to admin like LoginPage does
      window.location.href = '/admin';
      return true;
    } else {
      const errorMessage = response.data.error || 'API Token verification failed.';
      // Store error in localStorage and redirect to login page
      localStorage.setItem('embed_token_error', errorMessage);
      window.location.href = '/admin/login';
      return false;
    }
  } catch (err) {
    localStorage.removeItem('admin_api_token');
    localStorage.removeItem('admin_tokens');
    // Create error message for display
    const errorMessage = err.response?.data?.error || err.message || 'Token verification failed. Please check your token and try again.';
    // Store error in localStorage and redirect to login page
    localStorage.setItem('embed_token_error', errorMessage);
    window.location.href = '/admin/login';
    // Create a safe error object for logging and messaging
    const safeError = {
      message: errorMessage,
      status: err.response?.status || null,
      code: err.code || null
    };
    throw safeError;
  }
};

/**
 * Listen for token from parent window
 * @param {string[]} allowedOrigins - Array of allowed parent origins for security
 */
export const listenForMiraSolAdminUIToken = (allowedOrigins = []) => {
  if (!isInIframe()) return;

  const handleMessage = async (event) => {
    // Security check: validate origin if allowedOrigins is provided
    if (allowedOrigins.length > 0) {
      const isOriginAllowed = allowedOrigins.some(allowedOrigin => {
        // Direct match
        if (allowedOrigin === event.origin) {
          return true;
        }
        // Wildcard subdomain match (e.g., *.example.com)
        if (allowedOrigin.startsWith('*.') && event.origin.endsWith(allowedOrigin.substring(1))) {
          // Ensure it's a subdomain and not just a longer domain (e.g., not "badexample.com" for "*.example.com")
          const eventOriginParts = event.origin.split('.');
          const allowedOriginParts = allowedOrigin.split('.');
          if (eventOriginParts.length === allowedOriginParts.length) { // Simple check for same number of parts
            return true;
          }
        }
        return false;
      });

      if (!isOriginAllowed) {
        console.warn('Token message from unauthorized origin:', event.origin);
        return;
      }
    }

    // Check if this is a token message
    if (event.data && event.data.type === 'MIRASOL_ADMIN_UI_API_TOKEN' && event.data.payload) {
      try {
        await processEmbedToken(event.data.payload);
      } catch (error) {
        console.error('Failed to process embed token:', error);
        // Safely notify parent of error
        try {
          if (event.source) {
            // Use '*' as target origin if event.origin is null (file:// protocol)
            const targetOrigin = event.origin || '*';
            
            event.source.postMessage({
              type: 'MIRASOL_ADMIN_UI_API_TOKEN_ERROR',
              error: {
                message: String(error.message || 'Token processing failed'),
                status: error.status ? Number(error.status) : null,
                code: error.code ? String(error.code) : null
              }
            }, targetOrigin);
          }
        } catch (postMessageError) {
          console.warn('Failed to send error message to parent:', postMessageError);
        }
      }
    }
  };

  window.addEventListener('message', handleMessage);
  
  // Notify parent that we're ready to receive token
  window.parent.postMessage({
    type: 'MIRASOL_ADMIN_UI_READY'
  }, '*');

  // Return cleanup function
  return () => {
    window.removeEventListener('message', handleMessage);
  };
};

/**
 * Check if current session is an embedded session
 * @returns {boolean} True if this is an embedded session
 */
export const isEmbeddedSession = () => {
  return isInIframe();
};